import React from 'react';
import { observer } from 'mobx-react';
import ReactSelect, { GroupTypeBase, OptionTypeBase, Props, StylesConfig } from 'react-select';
import { CSSObject as EmotionCSSObject } from '@emotion/serialize';

export type Option = {
	label: string; 
	value: string;
};

export type SingleSelectProps<OptionType extends OptionTypeBase, GroupType extends GroupTypeBase<OptionType>> = Props<OptionType, false, GroupType> & {
	invalid?: boolean;
};

export type MultiSelectProps<OptionType extends OptionTypeBase, GroupType extends GroupTypeBase<OptionType>> = Props<OptionType, true, GroupType> & {
	invalid?: boolean;
};

//due to conflict of types in Select and emotion/serialize after upgrade to webpack 5 and babel we force to use any to keep unsing current version of select. 
//TODO: Please remove after upgrading 'react-select' package
/* eslint-disable @typescript-eslint/no-explicit-any */
type CSSObject = EmotionCSSObject & any;

// Colors are driven by the theme tokens in src/Scss/_theme-tokens.scss via var(--theme-*).
// react-select injects these as emotion inline styles; because var() resolves against the DOM
// (the menu is portaled under <html data-theme>), the same config themes both light and dark.
// Light token values match react-select's defaults, so light mode is unchanged.
function ReactSelectStyles<OptionType extends OptionTypeBase, IsMulti extends boolean, GroupType extends GroupTypeBase<OptionType>>(hasError: boolean): Partial<StylesConfig<OptionType, IsMulti, GroupType>> {
	return {
		control: (base: CSSObject) => ({
			...base,
			...{ minHeight: 'none', borderRadius: 0, fontSize: 12, backgroundColor: 'var(--theme-input-bg)', color: 'var(--theme-input-text)' },
			...(hasError ? { border: '2px solid red' } : { border: base.border, borderColor: 'var(--theme-input-border)' })
		}),
		singleValue: (base: CSSObject) => ({ ...base, color: 'var(--theme-input-text)' }),
		placeholder: (base: CSSObject) => ({ ...base, color: 'var(--theme-text-muted)' }),
		dropdownIndicator: (base: CSSObject) => ({ ...base, padding: '2px', color: 'var(--theme-text-muted)' }),
		indicatorSeparator: (base: CSSObject) => ({ ...base, backgroundColor: 'var(--theme-border-light)' }),
		input: (base: CSSObject) => ({ ...base, lineHeight: '17px', color: 'var(--theme-input-text)' }),
		valueContainer: (base: CSSObject) => ({ ...base, padding: '0 8px' }),
		clearIndicator: (base: CSSObject) => ({ ...base, padding: 0, color: 'var(--theme-text-muted)' }),
		multiValue: (base: CSSObject) => ({ ...base, margin: '0 2px 0 0', backgroundColor: 'var(--theme-subtle-bg)' }),
		multiValueLabel: (base: CSSObject) => ({ ...base, color: 'var(--theme-text)' }),
		menu: (base: CSSObject) => ({ ...base, zIndex: 29, fontSize: 12, backgroundColor: 'var(--theme-surface)' }),
		option: (base: CSSObject, state: CSSObject) => ({
			...base,
			backgroundColor: state.isSelected ? '#2684FF' : state.isFocused ? 'var(--theme-accent-hover-bg)' : 'transparent',
			color: state.isSelected ? '#fff' : 'var(--theme-text)',
			':active': { ...base[':active'], backgroundColor: 'var(--theme-accent-hover-bg)' }
		}),
		menuPortal: (base: CSSObject) => ({ ...base, zIndex: '99999!important', position:'absolute!important' }),
		container: (base: CSSObject) => ({...base, width: '100%'})
	};
}

@observer
export class Select<
			OptionType extends OptionTypeBase = { label: string; value: string }, 
			GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
		> extends React.PureComponent<SingleSelectProps<OptionType, GroupType>> {
	render() {
		const { invalid } = this.props;
		
		return (
			<ReactSelect<OptionType, false, GroupType>
				{...this.props}
				styles={{ ...ReactSelectStyles<OptionType, false, GroupType>(!!invalid), ...(this.props.styles || {}) }}
				menuPortalTarget={document.body}
			/>
		);
	}
}

@observer
export class MultiSelect<
		OptionType extends OptionTypeBase = { label: string; value: string }, 
		GroupType extends GroupTypeBase<OptionType> = GroupTypeBase<OptionType>
	> extends React.PureComponent<MultiSelectProps<OptionType, GroupType>> {
	render() {
		const { invalid } = this.props;

		return (
			<ReactSelect<OptionType, true, GroupType>
				{...this.props}
				styles={{ ...ReactSelectStyles<OptionType, true, GroupType>(!!invalid), ...(this.props.styles || {}) }}
				menuPortalTarget={document.body}
				isMulty
			/>
		);
	}
}
